import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/**
 * jhl-Bots (RAK-425): the review inbox „Überprüfung“. A bot asks its owner to decide, accept or
 * comment with request_review; the card waits in the owner's inbox and the answer goes back to the
 * session that asked. Contract: shared/rakazo/PRUEF-EINGANG-VERTRAG.md.
 */

/** Longest card title. */
export const REVIEW_TITLE_MAX_LENGTH = 120;
/** Longest summary (Markdown) and answer comment, in UTF-16 code units. */
export const REVIEW_TEXT_MAX_LENGTH = 4_000;
/** Most options of a decision. */
export const REVIEW_OPTIONS_MAX = 4;
/** Longest option label. */
export const REVIEW_OPTION_MAX_LENGTH = 80;
/** Most previews of one card. */
export const REVIEW_PREVIEWS_MAX = 8;
/** The fixed options of an acceptance card. */
export const REVIEW_ACCEPT_OPTIONS = ["ok", "zurueck"] as const;

/** What the card asks for: a decision between options, an acceptance (ok / zurueck), or a free comment. */
export const ReviewKind = z.enum(["entscheidung", "abnahme", "kommentar"]);
/** What the card asks for. */
export type ReviewKind = z.infer<typeof ReviewKind>;

/** Where a card stands; only offen and zurueckgestellt can still be answered. */
export const ReviewStatus = z.enum(["offen", "zurueckgestellt", "beantwortet", "zurueckgezogen"]);
/** Where a card stands. */
export type ReviewStatus = z.infer<typeof ReviewStatus>;

/** One preview tab: a stored artifact (file, image, HTML, live preview) or a plain link, never framed. */
export const ReviewPreviewSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("artifact"),
    artifactId: Id,
    name: z.string(),
    mimeType: z.string(),
    /** A live preview (expose_preview): it works only while the bot's computer runs. */
    live: z.boolean(),
  }),
  z.object({ kind: z.literal("link"), url: z.string().url() }),
]);
/** One preview tab of a card. */
export type ReviewPreview = z.infer<typeof ReviewPreviewSchema>;

/** The owner's answer to a card. */
export const ReviewAnswerSchema = z.object({
  option: z.string().nullable(),
  comment: z.string().nullable(),
  answeredAt: IsoDate,
});

/** A card in the review inbox, as its recipient sees it. */
export const ReviewRequestSchema = z.object({
  id: Id,
  botId: Id,
  botName: z.string(),
  /** The chat the card came from: a session's or the bot's main chat. */
  threadId: Id,
  sessionId: Id.nullable(),
  ticketRef: z.string().nullable(),
  parentId: Id.nullable(),
  title: z.string(),
  summary: z.string(),
  kind: ReviewKind,
  options: z.array(z.string()),
  previews: z.array(ReviewPreviewSchema),
  status: ReviewStatus,
  /** The recipient's order: lower first; reviews.reorder changes it. */
  position: z.number(),
  answer: ReviewAnswerSchema.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
/** A card in the review inbox. */
export type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

/** An open ask card (ask_user, approval, secret) shown in the inbox; it is answered in its chat. */
export const PendingAskSchema = z.object({
  botId: Id,
  botName: z.string(),
  threadId: Id,
  sessionId: Id.nullable(),
  messageId: Id,
  text: z.string(),
  createdAt: IsoDate,
});
/** An open ask card shown in the inbox. */
export type PendingAsk = z.infer<typeof PendingAskSchema>;

/** Input of reviews.list; without status the open and deferred cards. */
export const ListReviewsInput = z.object({ status: z.array(ReviewStatus).min(1).optional() });
/** Output of reviews.list: the cards and the open ask cards of the caller's bots. */
export const ListReviewsOutput = z.object({
  items: z.array(ReviewRequestSchema),
  asks: z.array(PendingAskSchema),
});
/** Input of reviews.answer; what is required depends on the card's kind. */
export const AnswerReviewInput = z.object({
  id: Id,
  option: z.string().max(REVIEW_OPTION_MAX_LENGTH).optional(),
  comment: z.string().max(REVIEW_TEXT_MAX_LENGTH).optional(),
});
/** Input of reviews.reorder: put the card before another, or last without beforeId. */
export const ReorderReviewInput = z.object({ id: Id, beforeId: Id.optional() });
/** Output of reviews.count: open cards plus open ask cards, for the badge. */
export const ReviewCountOutput = z.object({ open: z.number().int().nonnegative() });
